import * as PIXI from "pixi.js";
import { getScreenDensity } from "../../utils/screenDensity";

/**
 * Rasterise a text label ONCE and hand out its texture for ever after.
 *
 * ── The problem this exists for ──
 *
 * A spinning strip cell drew its number with a live `PIXI.Text`, restyled on
 * every wrap. Two things made that expensive, and both are properties of
 * PIXI's own setters:
 *
 *   - `Text.set style` has NO equality check. Unlike `set text`, it always
 *     allocates a new TextStyle and sets `dirty`, so re-assigning an identical
 *     style still forces a full re-rasterisation.
 *   - Reading `text.width` on a dirty Text runs `updateText()` SYNCHRONOUSLY —
 *     measure, resize the canvas, draw the glyph with its stroke and drop
 *     shadow, upload the texture — inside the strip's ticker.
 *
 * On a full Golden Mystery board that was ~115 canvas rasterisations and
 * texture uploads per second, which is why fifteen single-cell strips cost far
 * more than the whole 5x6 reel set: the reels draw sprites from one atlas with
 * BitmapText numbers, and rasterise nothing at all while moving.
 *
 * ── Why it reuses PIXI.Text rather than rendering to a RenderTexture ──
 *
 * A `PIXI.Text` IS a Sprite whose texture is its own canvas. So the cheapest
 * way to guarantee the label looks EXACTLY as it did is to keep using Text to
 * produce the pixels — once — and then point plain Sprites at the texture it
 * made. Same rasteriser, same style object, same resolution: byte-identical
 * output, with no renderer to pass in and no second code path to drift.
 *
 * The Text instances are kept alive in the cache precisely because they own
 * those textures. They are never added to the stage and never updated again.
 */

/** key -> { texture, width, height, text } */
const cache = new Map();

/**
 * The device density these labels are drawn at.
 *
 * ── PIXI.Text does NOT inherit the renderer's resolution ──
 *
 * `new PIXI.Text()` takes `PIXI.settings.RESOLUTION`, which is **1** and which
 * nothing in this project sets. The renderer runs at
 * `Math.min(window.devicePixelRatio, 2)` (Stage.js), so on any retina screen
 * every label here was rasterised at HALF the density it is drawn at, and then
 * magnified back — which is what made the spinning coin's number soft while the
 * same number looked fine everywhere it was drawn some other way.
 *
 * Same expression as the renderer's, deliberately: the two must agree, and a
 * label is not worth an import of the app just to read it back.
 *
 * Read once per call rather than at module load — a window dragged to a second
 * monitor changes devicePixelRatio, and the value is in the cache key, so the
 * labels are simply re-rasterised at the new density.
 */
// const labelResolution = () => Math.min(window.devicePixelRatio || 1, 2);
// ALWAYS 2x (Vladimir, 2026-10-01: "smoother") — on a 1x screen the labels
// were drawn at 1x and the spines' scales (a bone's hit bounce) stretched
// them, so thick outlines showed steps. Drawn at 2x and scaled down, the
// edges are anti-aliased; the cache holds a little more memory.
// Times the board's screen density: labels are drawn at native size and the
// board is magnified on ultra-wide screens (up to 2x), so the density keeps
// them sharp there. 1 on every other screen — unchanged. The resolution is in
// the cache key, so a density change simply draws new textures.
const labelResolution = () => 2 * getScreenDensity();

const keyFor = (label, styleKey, fontSize, resolution) =>
  `${styleKey}|${Math.round(fontSize)}|${resolution}|${label}`;

/**
 * The texture for one label, rasterised on first request and cached after.
 *
 * @param   {string} label     The string to draw.
 * @param   {object} style     A plain style object, already scaled.
 * @param   {string} styleKey  Identifies the style — skin name, variant, etc.
 *   Two different styles MUST NOT share a key, and the same style must always
 *   use the same one, or a label will be drawn with another's colours.
 * @param   {number} fontSize  Part of the key: a resize needs new pixels.
 * @returns {{texture: PIXI.Texture, width: number, height: number}|null}
 */
/**
 * PIXI.Text#drawLetterSpacing with the characters placed by their INK: each
 * one's drawn left edge `letterSpacing` after the previous one's right edge
 * (canvas measureText actualBoundingBox*, per character — no kerning, no side
 * bearings). The row is centred where PIXI measured the text
 * (TextMetrics._measureText: width + (n - 1) × letterSpacing), so the texture
 * size and the sprite's centre stay what PIXI made them.
 */
function drawEvenGaps(str, x, y, isStroke = false) {
  const ctx = this.context;
  const gap = this._style.letterSpacing || 0;
  const chars = PIXI.TextMetrics.graphemeSegmenter(str);
  const ink = chars.map((ch) => {
    const m = ctx.measureText(ch);
    return { left: m.actualBoundingBoxLeft || 0, right: m.actualBoundingBoxRight || 0 };
  });
  const inkWidth =
    ink.reduce((w, g) => w + g.left + g.right, 0) + gap * Math.max(0, chars.length - 1);
  const pixiWidth = ctx.measureText(str).width + gap * Math.max(0, chars.length - 1);
  let edge = x + (pixiWidth - inkWidth) / 2;
  chars.forEach((ch, i) => {
    const pen = edge + ink[i].left;
    if (isStroke) ctx.strokeText(ch, pen, y);
    else ctx.fillText(ch, pen, y);
    edge += ink[i].left + ink[i].right + gap;
  });
}

export const getLabelTexture = (label, style, styleKey, fontSize) => {
  if (label === "" || label == null) return null;

  const resolution = labelResolution();
  const key = keyFor(label, styleKey, fontSize, resolution);
  const hit = cache.get(key);
  if (hit) return hit;

  const text = new PIXI.Text(label, style);
  // Before `updateText` — this is what it rasterises AT. `width`/`height` stay
  // in logical pixels, so the squeeze maths downstream is unaffected.
  text.resolution = resolution;
  // // ── A style may switch the font's kerning off (`fontKerning: "none"`) ──
  // // (did nothing: PIXI resizes the canvas before drawing, and a resize
  // // resets every context setting)
  // if (style?.fontKerning && text.context && "fontKerning" in text.context) {
  //   text.context.fontKerning = style.fontKerning;
  // }
  // ── Even gaps (`evenGlyphGaps`) ── every character the SAME gap from the
  // next, measured between their drawn edges (Vladimir, 2026-09-30: "each
  // number has spacing, thats it"). PIXI spaces by advance widths, and the
  // empty side bearings inside them differ per glyph — Kanit's "1" and "."
  // carry a lot, "2" almost none — so the gaps read random.
  if (style?.evenGlyphGaps) text.drawLetterSpacing = drawEvenGaps;
  try {
    // Force the rasterisation NOW, so it happens while the board is being built
    // rather than on the frame a cell happens to wrap.
    //
    // Guarded because this is the one line that needs a real 2D context. A
    // headless environment or a lost context must not take the feature down with it —
    // the entry is still cached, the sprite still gets a texture, and the only
    // casualty is a number that has not been drawn yet.
    text.updateText(true);
  } catch {
    // Nothing to do — the texture is whatever PIXI left on it.
  }
  // ── Mipmaps (2026-10-02) ── the coin numbers are drawn at RIG size (and 2x)
  // and the spines shrink them a lot — in Hold & Win about 5x (0.64 cell art
  // scale x the room scale). Without mipmaps a 5x minification samples a few
  // texels per screen pixel, so the outlines came out stair-stepped
  // ("awfully pixelated"). Safe here, unlike on a live PIXI.Text (black
  // flash on re-upload): these canvases are drawn ONCE and never updated.
  // Set before the first upload (the first render), like assets.js does for
  // the UI sheets. Works on non-power-of-two sizes with WebGL2.
  const base = text.texture?.baseTexture;
  if (base && base.mipmap !== PIXI.MIPMAP_MODES.ON) {
    base.mipmap = PIXI.MIPMAP_MODES.ON;
  }

  const entry = {
    texture: text.texture,
    width: text.width || 0,
    height: text.height || 0,
    text,
  };
  cache.set(key, entry);
  return entry;
};

/**
 * Build a set of labels up front.
 *
 * The point of the whole cache: a strip spins a KNOWN pool, so every label it
 * can show is rasterised before it starts moving and none of it lands mid-spin.
 *
 * @param {Array<{label: string, style: object, styleKey: string,
 *   fontSize: number}>} entries
 */
export const warmLabelTextures = (entries) => {
  if (!Array.isArray(entries)) return;
  for (const entry of entries) {
    if (!entry?.label) continue;
    getLabelTexture(entry.label, entry.style, entry.styleKey, entry.fontSize);
  }
};

/**
 * ── KNOWN, and deliberately not solved here ──
 *
 * The key carries the font size, so every resize rasterises a fresh set and
 * the old one is never freed: a player who flips orientation twice holds three
 * sets for the session. Small — a few dozen short strings — but real.
 *
 * There WAS a `releaseLabelsExceptSize` here to drop the stale sizes. It is
 * gone because it could not be called safely: every Golden cell has its own
 * strip and they resize one at a time, so freeing after any single strip
 * destroys textures the others are still drawing. It needs a caller that knows
 * the whole board has finished resizing, and there is no such caller today.
 *
 * Deleted rather than left unwired, for the reason toStaticCell gives about its
 * own dead fallback: a path that never runs cannot be trusted when it finally
 * does.
 */

/** Testing / teardown only. */
export const clearLabelTextures = () => {
  for (const entry of cache.values()) {
    try {
      entry.text.destroy({ texture: true, baseTexture: true });
    } catch {
      // Already gone.
    }
  }
  cache.clear();
};

/** How many distinct labels are currently rasterised. */
export const labelTextureCount = () => cache.size;
